import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { Stack } from '@mantine/core';
import { t } from 'i18next';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useMemo, useState } from 'react';

import {
  AsyncDisplay,
  Button,
  CardV2 as Card,
  Icon,
  showError,
  Skeleton,
  Table,
  Toolbar,
} from '@feathr/components';
import { useStore, useUsageLimits } from '@feathr/extender/state';
import type { IPagination } from '@feathr/rachis';

import type { ITransformedMonthlyData } from './EmailLimits.types';
import EmailLimitsExportModal from './EmailLimitsExportModal';
import { transformMonthlyData } from './EmailLimitsPage.transformers';
import { monthlyBreakdownColumns } from './MonthlyBreakdownCard.columns';

const tableSkeleton = (
  <Stack gap={8}>
    <Skeleton height={44} width={'100%'} />
    <Skeleton height={44} width={'100%'} />
    <Skeleton height={44} width={'100%'} />
    <Skeleton height={44} width={'100%'} />
    <Skeleton height={44} width={'100%'} />
  </Stack>
);

function MonthlyBreakdownCard(): JSX.Element {
  const { EmailUsageRecords } = useStore();
  const { emailSendLimit } = useUsageLimits();
  const [monthlyData, setMonthlyData] = useState<ITransformedMonthlyData[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [pagination, setPagination] = useState<IPagination>({
    page: 0,
    page_size: 100,
  });

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const monthlyUsageRecords = EmailUsageRecords.list({ pagination });
        await when(() => !monthlyUsageRecords.isPending);

        // Transform the data
        const transformedMonthly = monthlyUsageRecords.models.map((monthly) =>
          transformMonthlyData(monthly.toJS()),
        );

        // Sort by record_date in descending order (most recent first)
        const sortedMonthlyData = transformedMonthly.sort(
          (a, b) => new Date(b.record_date).getTime() - new Date(a.record_date).getTime(),
        );

        setMonthlyData(sortedMonthlyData);
        setTotalPages(monthlyUsageRecords.pagination.pages ?? 0);
      } catch (error) {
        showError(error, t);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [pagination, emailSendLimit]);

  const handleExport = (): void => {
    setIsExportModalOpen(true);
  };

  // Calculate total incremental costs
  const totalIncrementalCosts = useMemo(() => {
    return monthlyData.reduce((sum, row) => sum + row.incrementalCosts, 0);
  }, [monthlyData]);

  function onPageSizeChange(newPageSize: number): void {
    // Reset to first page when page size changes
    setPagination({ page: 0, page_size: newPageSize });
  }

  function onPageChange(newPage: number): void {
    setPagination({ page: newPage, page_size: pagination.page_size });
  }

  const monthlyColumns = useMemo(
    () => monthlyBreakdownColumns(totalIncrementalCosts),
    [totalIncrementalCosts],
  );

  return (
    <Card width={'full'}>
      <Card.Header title={'Monthly Breakdown'}>
        <Toolbar>
          <Button onClick={handleExport} prefix={<Icon icon={faDownload} />} type={'icon-outlined'}>
            {t('Export')}
          </Button>
        </Toolbar>
      </Card.Header>
      <Card.Content>
        <AsyncDisplay isLoading={isLoading} skeleton={tableSkeleton}>
          <Table<ITransformedMonthlyData>
            columns={monthlyColumns}
            idKey={'id'}
            items={monthlyData}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            page={pagination.page}
            pages={totalPages}
            pageSize={pagination.page_size}
          />
        </AsyncDisplay>
      </Card.Content>
      <EmailLimitsExportModal
        exportType={'monthly'}
        onClose={(): void => setIsExportModalOpen(false)}
        opened={isExportModalOpen}
      />
    </Card>
  );
}

export default observer(MonthlyBreakdownCard);
